import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { Pencil, Loader2, Zap } from "lucide-react";
import { RateLimitDisplay } from "./RateLimitDisplay";
import { RateLimitEditor } from "./RateLimitEditor";

export type RateLimitEntry = {
  type: string;
  limit: number;
  interval: number;
};

type Props = {
  title?: string;
  description?: string;
  queryLimits: RateLimitEntry[];
  onSave: (limits: RateLimitEntry[]) => Promise<void>;
  isLoading?: boolean;
};

export function RateLimits({
  title = "Rate Limits",
  description = "Rate limits for different API call types",
  queryLimits,
  onSave,
  isLoading = false,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = async (limits: RateLimitEntry[]) => {
    await onSave(limits);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-chromium-mist" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="admin-card-heading">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <RateLimitEditor
            queryLimits={queryLimits}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        ) : (
          <RateLimitDisplay queryLimits={queryLimits} />
        )}
      </CardContent>
    </Card>
  );
}
